import React from 'react';
import { NextRouter, useRouter } from 'next/router';
import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  ChevronLeftIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Layout } from '../../../components/common';
import useCampaign from '../../../lib/hooks/campaigns';
import { useTypesBySlug } from '../../../lib/hooks/types';
import Custom404 from '../../../pages/404';
import Breadcrumb from './Breadcrumb';
import Sidebar from './Sidebar';
import Navigation from './Navigation';
import navigationItems, {
  CUSTOM_TYPES_NAME,
  NavigationItem,
  NavigationLink,
} from './navigationItems';
import { CustomType } from '@prisma/client';

function generateNavigationItems(
  router: NextRouter,
  types?: CustomType[],
): NavigationItem[] {
  const { teamSlug, campaignSlug } = router.query;

  return navigationItems.map((item) => {
    if (item.name === CUSTOM_TYPES_NAME) {
      if (types?.length > 0) {
        const typesNav = types
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((type) => {
            const href = `/${teamSlug}/${campaignSlug}/types/${type.slug}`;

            return {
              name: type.name,
              href,
              current: href === router.asPath || href === `${router.asPath}/`,
            } as NavigationLink;
          });

        const subNav = typesNav.concat(
          item.children.map((child) => {
            const href = `/${teamSlug}/${campaignSlug}${child.href}`;

            return {
              ...child,
              href,
              current: href === router.asPath || href === `${router.asPath}/`,
            } as NavigationLink;
          }),
        );

        const current = subNav.some((child) => child.current);

        return {
          ...item,
          current,
          children: subNav,
        } as NavigationItem;
      } else {
        const href = `/${teamSlug}/${campaignSlug}${item.href}`;
        return {
          ...item,
          href,
          current: href === router.asPath || href === `${router.asPath}/`,
        } as NavigationItem;
      }
    } else {
      if (item.href) {
        const href = `/${teamSlug}/${campaignSlug}${item.href}`;
        return {
          ...item,
          href,
          current: href === router.asPath || href === `${router.asPath}/`,
        } as NavigationItem;
      } else {
        const subNav = item.children.map((child) => {
          const href = `/${teamSlug}/${campaignSlug}${child.href}`;
          return {
            ...child,
            href,
            current: href === router.asPath || href === `${router.asPath}/`,
          } as NavigationLink;
        });

        const current = subNav.some((child) => child.current);

        return {
          ...item,
          current,
          children: subNav,
        } as NavigationItem;
      }
    }
  });
}

function getPageTitle(navigation: NavigationItem[]) {
  const currentItem = navigation.find((item) => item.current);

  if (!currentItem) return null;

  return currentItem.href
    ? currentItem.name
    : currentItem.children.find((child) => child.current)?.name;
}

function CampaignLayout({ children, pageProps }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const router = useRouter();
  const { teamSlug, campaignSlug, typeSlug, channel } = router.query;

  const {
    campaign,
    isLoading: isLoadingCampaign,
    isError: isErrorCampaign,
  } = useCampaign(campaignSlug as string, teamSlug as string);

  // FIXME: Fetch this with campaign (N-1 queries)
  const {
    types,
    isLoading: isLoadingTypes,
    isError: isErrorTypes,
  } = useTypesBySlug(campaignSlug as string);

  const navigation = generateNavigationItems(router, types);

  const pageTitle = getPageTitle(navigation) || channel || '';

  const isLoading = isLoadingCampaign || isLoadingTypes;

  if (isLoading) {
    return (
      <Layout
        title={`${campaign?.name || ''} - ${pageTitle}`}
        pageProps={pageProps}
      >
        <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:bottom-0 md:top-16">
          <div className="flex-1 flex flex-col min-h-0 border-r border-gray-200 bg-white" />
        </div>
      </Layout>
    );
  }

  const isError =
    isErrorCampaign ||
    isErrorTypes ||
    (typeSlug &&
      types &&
      types.findIndex((type) => type.slug === typeSlug) === -1);

  if (isError) {
    return <Custom404 />;
  }

  const title = channel
    ? `${channel} | Channel Details`
    : `${pageTitle || ''} | ${campaign?.name || ''}`;

  return (
    <Layout title={title} pageProps={pageProps}>
      <div>
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 flex z-40 md:hidden"
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 right-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                  <nav className="mt-5 px-2 space-y-1">
                    <Navigation navigation={navigation} />
                  </nav>
                </div>
              </div>
            </Transition.Child>
            <div className="flex-shrink-0 w-14">
              {/* Force sidebar to shrink to fit close icon */}
            </div>
          </Dialog>
        </Transition.Root>

        <div className="flex">
          <Sidebar navigation={navigation} />
          <div className="flex flex-col flex-1">
            <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-gray-100">
              <button
                type="button"
                className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-rum-500"
                onClick={() => setSidebarOpen(true)}
              >
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <main className="flex-1">
              <div className="py-6">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                  <div>
                    <nav className="sm:hidden" aria-label="Back">
                      <a
                        onClick={() => router.back()}
                        className="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
                      >
                        <ChevronLeftIcon
                          className="flex-shrink-0 -ml-1 mr-1 h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                        Back
                      </a>
                    </nav>
                    <Breadcrumb campaign={campaign} />
                  </div>
                  <h1 className="mt-2 text-3xl font-semibold text-gray-900">
                    {pageTitle}
                  </h1>
                </div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                  <div className="py-4">
                    {isLoading
                      ? 'Loading...'
                      : React.cloneElement(children, {
                          campaign,
                        })}
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default CampaignLayout;
